import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OrderService } from '../utility/services';
import { useDebouncedEffect } from './debounce';
import { CommonConstant, OrderUrlKeys } from '../utility/constants';
import { CommonUtility } from '../utility/common';

const DEFAULT_SORT = {
  sortBy: 'timeAcknowledged',
  sortOrder: CommonConstant.sortDirection.descending,
};

function resolveSort(sort) {
  if (sort && typeof sort === 'object' && sort.sortBy && sort.sortOrder) {
    return { sortBy: sort.sortBy, sortOrder: sort.sortOrder };
  }
  return DEFAULT_SORT;
}

export const OrderHook = ({ type, customerId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const requestRef = useRef(0);
  const [filter, setFilter] = useState({
    limit: null,
    page: 1,
    search: '',
    sort: DEFAULT_SORT,
  });

  const fetchData = async (cancelToken, requestId) => {
    setLoading(true);
    try {
      const trimmedSearch = (filter.search || '').trim();
      const { sortBy, sortOrder } = resolveSort(filter.sort);
      const params = {
        page: filter.page,
        limit: filter.limit,
        sortBy,
        sortOrder,
      };

      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      if (filter.customerOrganizationId) {
        params.customerOrganizationId = filter.customerOrganizationId;
      }
      if (
        type === 'pending-fulfillment' &&
        (filter.oos === 'true' || filter.oos === 'false')
      ) {
        params.oos = filter.oos;
      }

      const hasSearch = Boolean(trimmedSearch);
      const hasCustomer = Boolean(filter.customerOrganizationId);
      if ((type === 'shipped' && !hasSearch && !hasCustomer) || type === 'paused') {
        params.status = OrderUrlKeys[type];
      }

      const res = await OrderService.get(params, cancelToken);
      if (requestRef.current !== requestId) return;

      setData(res?.data);
      setTotalData(res?.totalCount);
      setTotalPages(res?.totalPages);
    } catch (error) {
      if (requestRef.current !== requestId) return;

      setError(error);
    } finally {
      if (requestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useDebouncedEffect(
    () => {
      let request = null;

      if (type && filter.limit) {
        request = axios.CancelToken.source();
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;

        fetchData(request.token, requestId);
      }

      return () => {
        if (request) {
          request.cancel();
        }
      };
    },
    [filter, type, refreshKey],
    500,
  );

  useEffect(() => {
    filterChanged('customerOrganizationId', customerId);
  }, [customerId]);

  const pageChanged = page => {
    setFilter(prev => ({ ...prev, page }));
  };

  const filterChanged = (key, value) => {
    setFilter(prev => ({ ...prev, page: 1, [key]: value }));
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const onSortChange = sortObject => {
    if (!CommonUtility.isValidObject(sortObject)) return;
    const sortKey = Object.keys(sortObject)[0];
    if (!sortKey) return;

    setFilter(prev => ({
      ...prev,
      page: 1,
      sort: { sortBy: sortKey, sortOrder: sortObject[sortKey] },
    }));
  };

  const parsedSort = useMemo(() => {
    const sort = resolveSort(filter.sort);
    return { [sort.sortBy]: sort.sortOrder };
  }, [filter.sort]);

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const updateAllFilter = obj => {
    setFilter(prev => {
      const updates = { ...obj };
      if (typeof updates.sort === 'string') {
        const parsed = CommonUtility.tryParseJSONObject(updates.sort);
        if (parsed) {
          updates.sort = parsed;
        } else {
          delete updates.sort;
        }
      }
      if (updates.page != null) {
        updates.page = Number(updates.page) || 1;
      }
      if (updates.limit != null) {
        updates.limit = Number(updates.limit) || prev.limit;
      }
      const next = { ...prev, page: 1, ...updates };
      next.sort = resolveSort(next.sort);
      return next;
    });
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    onSortChange,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    parsedSort,
    refresh,
    updateAllFilter,
  };
};

export const OrderDetailHook = (id, providerId) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await OrderService.orderDetail(id, { providerId });
      setData(res?.data || {});
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && providerId) {
      fetchData();
    }
  }, [id, providerId]);

  return { data, loading, error, refresh: fetchData };
};

export const OrderAllocationHook = id => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await OrderService.allocationLogs(id);
      setData(res?.data || []);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  return { data, loading, error, refresh: fetchData };
};
